import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/**
 * PageHeader — shared page-header primitive for Directory list pages.
 *
 * Replaces the ad-hoc flexWrap + description + primary-CTA row each
 * list page hand-rolled. Opt-in `sticky` prop for pages that scroll
 * long enough to earn a fixed action anchor; off by default so the
 * header cannot fight a container whose overflow it does not own.
 *
 * Slots:
 *  - title      : h5 heading (optional — child pages sitting under a
 *                 higher-level hero often omit their own title)
 *  - titleInfo  : optional tooltip string that renders as an (i) icon
 *                 next to the title — for terse titles that need one
 *                 sentence of context without bloating the subtitle
 *  - subtitle   : body2 secondary description (optional)
 *  - action     : right-side node — usually the primary CTA button
 *
 * Uses MUI theme tokens only; no custom colors.
 */
export default function PageHeader({
  title,
  titleInfo,
  subtitle,
  action,
  sticky = false,
  sx,
}) {
  return (
    <Box
      component="header"
      sx={{
        ...(sticky
          ? {
              position: "sticky",
              top: 0,
              zIndex: 1,
              backgroundColor: "background.default",
            }
          : null),
        pt: 1,
        pb: 1.5,
        mb: 2,
        ...sx,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "flex-start" }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {title && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography
                variant="h5"
                component="h1"
                sx={{ fontWeight: 600, letterSpacing: "-0.005em" }}
              >
                {title}
              </Typography>
              {titleInfo && (
                <Tooltip title={titleInfo} arrow enterDelay={400}>
                  <IconButton
                    size="small"
                    aria-label={`About ${typeof title === "string" ? title : "this page"}`}
                    sx={{ color: "text.disabled", p: 0.25 }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}
          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, maxWidth: 720 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && (
          <Box sx={{ flexShrink: 0, alignSelf: { xs: "flex-start", sm: "center" } }}>
            {action}
          </Box>
        )}
      </Stack>
    </Box>
  );
}
